import * as THREE from "three";
import { OrbitControls } from "three/orbitcontrols";
import { GLTFLoader } from "three/gltfloader";
import { AsciiEffect } from "three/ascii";
import { CSS2DRenderer, CSS2DObject } from "three/css2drenderer";

function isIOSDevice() {
  return (
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) || // iPad on iOS 13+
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) // iPad on iPadOS
  );
}

async function initGlobe() {
  let earthCenter = new THREE.Vector3(0, 0, 0);
  let locationTarget = new THREE.Vector3(0, 0, 0);

  const container = document.querySelector(".globe__canvas");
  const parentContainer = document.querySelector(".wrg.hero-globe");

  if (!container || !parentContainer) {
    return;
  }

  // Track visibility for effect rendering optimization
  let isGlobeVisible = true;
  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isGlobeVisible = entry.isIntersecting;
      });
    },
    {
      threshold: 0.1, // Trigger when at least 10% is visible
    },
  );
  visibilityObserver.observe(container);

  // Track camera/controls state to determine if effect rendering is necessary
  let lastCameraPosition = new THREE.Vector3();
  let lastCameraRotation = new THREE.Euler();
  let lastControlsAzimuthal = 0;
  let lastControlsPolar = 0;
  let needsEffectRender = true; // Start with true to render initial frame

  // Store initial viewport height before any scroll (prevents Safari address bar issues)
  // This captures the height when address bar is hidden (full viewport)
  const initialViewportHeight = window.visualViewport
    ? window.visualViewport.height
    : window.innerHeight;

  // Get stable height that doesn't change with Safari address bar
  const getStableHeight = () => {
    // Prefer container's actual height from CSS (uses vh/dvh units set initially)
    if (container.clientHeight && container.clientHeight > 0) {
      return container.clientHeight;
    }
    // Fallback to stored initial height (most stable for Safari)
    return initialViewportHeight;
  };

  const width = container.clientWidth || window.innerWidth;
  const height = getStableHeight();

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  const camera = new THREE.PerspectiveCamera(80, width / height, 0.1, 1000);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
  });
  renderer.setSize(width, height);
  renderer.setPixelRatio(1);

  renderer.shadowMap.enabled = false;

  const effect = new AsciiEffect(renderer, " .", { invert: true });
  effect.setSize(width, height);
  effect.domElement.style.color = "white";
  effect.domElement.style.backgroundColor = "#080807";
  effect.domElement.style.userSelect = "none";
  effect.domElement.style.cursor = "none";

  container.appendChild(effect.domElement);

  const controls = new OrbitControls(camera, effect.domElement);

  // Detect tablet device (iPad or Android tablet)
  const isTablet =
    /iPad/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) || // iPad on iOS 13+
    (window.innerWidth >= 768 &&
      window.innerWidth <= 1024 &&
      navigator.maxTouchPoints > 1) || // Android tablets
    (window.innerWidth > 1024 && navigator.maxTouchPoints > 1); // Large tablets

  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.enableRotate = true;
  controls.autoRotate = false;
  controls.autoRotateSpeed = 0.75;
  controls.enabled = false;

  if (!isTablet) {
    let isTwoFingerTouch = false;
    effect.domElement.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches.length === 2) {
          isTwoFingerTouch = true;
          e.preventDefault();
        }
      },
      { passive: false },
    );

    effect.domElement.addEventListener(
      "touchmove",
      (e) => {
        if (isTwoFingerTouch && e.touches.length === 2) {
          e.preventDefault();
        }
      },
      { passive: false },
    );

    effect.domElement.addEventListener(
      "touchend",
      (e) => {
        if (e.touches.length < 2) {
          isTwoFingerTouch = false;
        }
      },
      { passive: false },
    );
  }

  // Allow infinite vertical rotation by extending limits beyond normal range
  // This allows continuous vertical rotation like horizontal rotation
  controls.minPolarAngle = -Infinity; // No lower limit
  controls.maxPolarAngle = Infinity; // No upper limit

  // State objects for drag controls (passed by reference)
  const dragState = {
    isDragging: false,
    shouldResetRotations: false,
    interactionsEnabled: false,
  };
  let previousMousePosition = { x: 0, y: 0 };
  let rotationVelocity = { y: 0 };
  const damping = 0.95;
  const rotationSpeed = 0.005;
  const lerpSpeed = 0.1;

  let spherical = new THREE.Spherical();
  let cameraTarget = new THREE.Vector3();

  const css2dRenderer = new CSS2DRenderer();
  css2dRenderer.setSize(width, height);
  css2dRenderer.domElement.className = "globe__labels-container"; // CSS will handle positioning
  css2dRenderer.domElement.style.pointerEvents = "none";
  css2dRenderer.domElement.style.overflow = "visible"; // Allow labels to overflow
  parentContainer.appendChild(css2dRenderer.domElement);

  const locations = [
    {
      name: "Seattle - WA - USA",
      lat: 27.6062,
      lon: -148,
      color: 0x1e90ff,
    },
    {
      name: "Kochi - Kerala - India",
      lat: 9.9781,
      lon: 76.2999,
      color: 0xff00ff,
    },
    {
      name: "Chennai - TN - India",
      lat: 12.9671,
      lon: 80.2514,
      color: 0xff9900,
    },
    {
      name: "Bogotá - CO - Colombia",
      lat: 4.710989,
      lon: -74.072092,
      color: 0x32cd32,
    },
  ];
  const radius = 100;

  function latLonToVector3(lat, lon, radius) {
    const latRad = (90 - lat) * (Math.PI / 180);
    const lonRad = (lon + 180) * (Math.PI / 180);
    const x = radius * Math.sin(latRad) * Math.cos(lonRad) * 0.1;
    const y = radius * Math.cos(latRad) * 0.11;
    const z = radius * Math.sin(latRad) * Math.sin(lonRad) * 0.1;
    return new THREE.Vector3(x, y, z);
  }

  const css2dLabels = [];
  function createPillLabel(text) {
    const el = document.createElement("div");
    el.className = "hero-globe__label";
    el.textContent = text;
    el.style.cursor = "pointer";
    return el;
  }
  function createLocationLabels() {
    locations.forEach((location, i) => {
      const pill = createPillLabel(location.name);
      pill.style.pointerEvents = "auto";
      pill.style.touchAction = "manipulation";

      const handleLabelClick = () => {
        animateToLocation(i);
      };

      pill.addEventListener("click", handleLabelClick);
      pill.addEventListener("touchend", (e) => {
        e.preventDefault();
        handleLabelClick();
      });

      const cssObject = new CSS2DObject(pill);
      const position = latLonToVector3(location.lat, location.lon, radius + 5);
      position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 0.61);
      position.add(earthCenter);
      cssObject.position.copy(position);
      css2dLabels.push(cssObject);
      scene.add(cssObject);
    });
  }
  function moveCameraToLocation(lat, lon, distance = cameraDistance) {
    const target = latLonToVector3(lat, lon, 1);
    target.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 0.61);
    const cameraPos = target.clone().multiplyScalar(distance).add(earthCenter);

    // camera.position.copy(cameraPos);
    controls.enabled = false;
  }

  const { lat: lat1, lon: lon1 } = locations[0];
  const { lat: lat2, lon: lon2 } = locations[1];
  const { lat: lat3, lon: lon3 } = locations[2];
  const { lat: lat4, lon: lon4 } = locations[3];

  function setActiveLabel(index) {
    css2dLabels.forEach((label, i) => {
      if (label.element) {
        if (i === index) {
          label.element.classList.add("active");
        } else {
          label.element.classList.remove("active");
        }
      }
    });
  }

  let currentGeo = { lat: lat1, lon: lon1 };
  let currentActiveIndex = 0;

  function getCameraDistance() {
    if (window.innerWidth <= 500) {
      return 350;
    } else if (window.innerWidth <= 900) {
      return 280;
    } else {
      return 190;
    }
  }

  let cameraDistance = getCameraDistance();
  function updateCamera() {
    moveCameraToLocation(currentGeo.lat, currentGeo.lon, cameraDistance);
  }
  let cameraAnimProgress = { value: 0 };

  function animateToLocation(index) {
    const location = locations[index];

    controls.enabled = false;
    currentActiveIndex = index;

    const targetPosition = latLonToVector3(location.lat, location.lon, 1);
    targetPosition.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 0.61);
    const targetCameraPos = targetPosition
      .clone()
      .multiplyScalar(cameraDistance)
      .add(earthCenter);

    const startOffset = new THREE.Vector3();
    // startOffset.copy(camera.position).sub(locationTarget);
    const startSpherical = new THREE.Spherical().setFromVector3(startOffset);

    const endOffset = new THREE.Vector3();
    endOffset.copy(targetCameraPos).sub(locationTarget);
    const endSpherical = new THREE.Spherical().setFromVector3(endOffset);

    let startTheta = startSpherical.theta;
    let endTheta = endSpherical.theta;

    const delta = endTheta - startTheta;
    if (Math.abs(delta) > Math.PI) {
      if (delta > 0) {
        startTheta += Math.PI * 2;
      } else {
        endTheta += Math.PI * 2;
      }
    }

    gsap.killTweensOf(cameraAnimProgress);
    cameraAnimProgress.value = 0;

    gsap.to(cameraAnimProgress, {
      value: 1,
      duration: 1.5,
      ease: "power2.inOut",
      onUpdate: () => {
        const progress = cameraAnimProgress.value;

        const currentTheta = startTheta + (endTheta - startTheta) * progress;
        const currentPhi =
          startSpherical.phi +
          (endSpherical.phi - startSpherical.phi) * progress;
        const currentRadius =
          startSpherical.radius +
          (endSpherical.radius - startSpherical.radius) * progress;

        const animSpherical = new THREE.Spherical(
          currentRadius,
          currentPhi,
          currentTheta,
        );
        const offset = new THREE.Vector3().setFromSpherical(animSpherical);

        camera.position.copy(locationTarget).add(offset);
        camera.lookAt(locationTarget);
      },
      onComplete: () => {
        controls.enabled = true;
        currentGeo.lat = location.lat;
        currentGeo.lon = location.lon;
      },
    });

    setActiveLabel(index);
  }

  let scrollDirection = 1;
  let scrollInitialized = false;

  function initScrollTrigger() {
    if (scrollInitialized) return;
    scrollInitialized = true;

    const activeLocation = locations[currentActiveIndex];

    const targetPosition = latLonToVector3(
      activeLocation.lat,
      activeLocation.lon,
      1,
    );
    targetPosition.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 0.61);
    const targetCameraPos = targetPosition
      .clone()
      .multiplyScalar(cameraDistance)
      .add(earthCenter);

    // Calculate end position (final target)
    const endOffset = new THREE.Vector3();
    endOffset.copy(targetCameraPos).sub(locationTarget);
    const endSpherical = new THREE.Spherical().setFromVector3(endOffset);

    // Get current camera position
    const currentOffset = new THREE.Vector3();
    // currentOffset.copy(camera.position).sub(locationTarget);
    const currentSpherical = new THREE.Spherical().setFromVector3(
      currentOffset,
    );

    // Set initial camera position closer to final position (90% interpolated)
    // This reduces the movement and makes animation smoother
    const initialProgress = 0.7;
    const startSpherical = new THREE.Spherical();
    startSpherical.radius =
      currentSpherical.radius +
      (endSpherical.radius - currentSpherical.radius) * initialProgress;
    startSpherical.theta =
      currentSpherical.theta +
      (endSpherical.theta - currentSpherical.theta) * initialProgress;
    startSpherical.phi =
      currentSpherical.phi +
      (endSpherical.phi - currentSpherical.phi) * initialProgress;

    const initialOffset = new THREE.Vector3().setFromSpherical(startSpherical);
    // camera.position.copy(locationTarget).add(initialOffset);
    camera.lookAt(locationTarget);

    let startTheta = startSpherical.theta;
    let endTheta = endSpherical.theta;

    const delta = endTheta - startTheta;
    if (Math.abs(delta) > Math.PI) {
      if (delta > 0) {
        startTheta += Math.PI * 2;
      } else {
        endTheta += Math.PI * 2;
      }
    }

    gsap.killTweensOf(cameraAnimProgress);
    cameraAnimProgress.value = 0;

    gsap.to(cameraAnimProgress, {
      value: 1,
      duration: 1.5,
      ease: "power3.out",
      onUpdate: () => {
        const progress = cameraAnimProgress.value;

        const currentTheta = startTheta + (endTheta - startTheta) * progress;
        const currentPhi =
          startSpherical.phi +
          (endSpherical.phi - startSpherical.phi) * progress;
        const currentRadius =
          startSpherical.radius +
          (endSpherical.radius - startSpherical.radius) * progress;

        const animSpherical = new THREE.Spherical(
          currentRadius,
          currentPhi,
          currentTheta,
        );
        const offset = new THREE.Vector3().setFromSpherical(animSpherical);

        camera.position.copy(locationTarget).add(offset);
        camera.lookAt(locationTarget);
      },
      onComplete: () => {
        currentGeo.lat = activeLocation.lat;
        currentGeo.lon = activeLocation.lon;
        // Enable interactions after initial animation completes
        if (window.enableGlobeInteractions) {
          window.enableGlobeInteractions();
        }
      },
    });

    const isMobile =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;

    // Check if device is iPad or has touch capability (for disabling scroll animation)
    const isTouchDevice =
      /iPad/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) || // iPad on iOS 13+
      (navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 1 &&
        "ontouchstart" in window);
  }
  // OrbitControls handles its own touch events - no custom handler needed
  // initScrollTrigger() is called after model loads for iOS devices
  // For non-iOS, it's called in the loader callback
  const isIOS = isIOSDevice();
  if (!isIOS) {
    initScrollTrigger();
  }

  // Function to enable OrbitControls after initial animation
  window.enableGlobeInteractions = () => {
    if (controls) {
      controls.enabled = true;
    }
  };

  const loader = new GLTFLoader();
  let earthModel = null;

  // Globe entrance: start from front-right, animate to final position and rotation
  const GLOBE_FINAL_POSITION = new THREE.Vector3(-10, -12.5, 0);
  const GLOBE_FINAL_ROTATION_Y = -Math.PI / 0.51;
  const GLOBE_ENTRANCE_START = new THREE.Vector3(-10, -13.5, 0);
  const GLOBE_ENTRANCE_START_ROTATION_Y = GLOBE_FINAL_ROTATION_Y * 0.88;
  const GLOBE_ENTRANCE_DURATION = 0.9;

  loader.load(
    "assets/images/earth/earth-hollow.gltf",
    (gltf) => {
      earthModel = gltf.scene;
      earthModel.position.copy(GLOBE_FINAL_POSITION);
      earthModel.rotation.y = GLOBE_FINAL_ROTATION_Y;
      earthModel.scale.set(0.12, 0.13, 0.12);

      scene.add(earthModel);
      earthModel.updateMatrixWorld(true);

      const boundingBox = new THREE.Box3().setFromObject(earthModel);
      earthCenter = new THREE.Vector3();
      boundingBox.getCenter(earthCenter);

      earthModel.traverse((child) => {
        if (child.isMesh) {
          child.material.transparent = false;
          child.material.side = THREE.FrontSide;
          child.material.depthTest = true;
          child.material.depthWrite = true;
          child.geometry.computeBoundingBox();
        }
      });
      /* Uncomment createLocationLabels to show the locations on the globe */
      // createLocationLabels();
      locationTarget = new THREE.Vector3();
      locationTarget.copy(earthModel.position);
      locationTarget.y = 2;

      controls.target.copy(locationTarget);
      controls.update();

      // Globe appears at India; camera and look-at are set for final position
      const indiaLocation = locations[1];
      currentGeo.lat = indiaLocation.lat;
      currentGeo.lon = indiaLocation.lon;
      currentActiveIndex = 1;
      moveCameraToLocation(
        indiaLocation.lat,
        indiaLocation.lon,
        cameraDistance,
      );
      setActiveLabel(1);

      // Start globe at front-right with start rotation, then animate to final position and rotation
      earthModel.position.copy(GLOBE_ENTRANCE_START);
      earthModel.rotation.y = GLOBE_ENTRANCE_START_ROTATION_Y;

      const runAfterGlobeEntrance = () => {
        const isIOS = isIOSDevice();
        if (isIOS) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                earthModel &&
                scene.children.includes(earthModel) &&
                !scrollInitialized
              ) {
                initScrollTrigger();
              }
            });
          });
        } else {
          if (!scrollInitialized) {
            initScrollTrigger();
          }
        }
      };

      const tl = gsap.timeline({ onComplete: runAfterGlobeEntrance });
      tl.to(
        earthModel.position,
        {
          x: GLOBE_FINAL_POSITION.x,
          y: GLOBE_FINAL_POSITION.y,
          z: GLOBE_FINAL_POSITION.z,
          duration: GLOBE_ENTRANCE_DURATION,
          ease: "power2.out",
        },
        0,
      );
      tl.to(
        earthModel.rotation,
        {
          y: GLOBE_FINAL_ROTATION_Y,
          duration: GLOBE_ENTRANCE_DURATION,
          ease: "power2.out",
        },
        0,
      );
    },
    undefined,
    undefined,
  );

  function animate() {
    controls.update();

    // Check if camera position has changed
    const cameraPositionChanged =
      Math.abs(camera.position.x - lastCameraPosition.x) > 0.01 ||
      Math.abs(camera.position.y - lastCameraPosition.y) > 0.01 ||
      Math.abs(camera.position.z - lastCameraPosition.z) > 0.01;

    // Check if camera rotation has changed
    const cameraRotationChanged =
      Math.abs(camera.rotation.x - lastCameraRotation.x) > 0.001 ||
      Math.abs(camera.rotation.y - lastCameraRotation.y) > 0.001 ||
      Math.abs(camera.rotation.z - lastCameraRotation.z) > 0.001;

    // Check if OrbitControls have changed (user interaction or auto-rotate)
    const currentAzimuthal = controls.getAzimuthalAngle();
    const currentPolar = controls.getPolarAngle();
    const controlsChanged =
      Math.abs(currentAzimuthal - lastControlsAzimuthal) > 0.001 ||
      Math.abs(currentPolar - lastControlsPolar) > 0.001;

    // Check if earth model is rotating or resetting
    let earthModelChanging = false;
    if (earthModel) {
      if (dragState.shouldResetRotations) {
        const threshold = 0.001;

        earthModel.rotation.x += (0 - earthModel.rotation.x) * lerpSpeed;
        earthModel.rotation.z += (0 - earthModel.rotation.z) * lerpSpeed;

        earthModelChanging =
          Math.abs(earthModel.rotation.x) > threshold ||
          Math.abs(earthModel.rotation.z) > threshold;

        if (
          Math.abs(earthModel.rotation.x) < threshold &&
          Math.abs(earthModel.rotation.z) < threshold
        ) {
          earthModel.rotation.x = 0;
          earthModel.rotation.z = 0;
          dragState.shouldResetRotations = false;
        }
      }
    }

    // Determine if effect rendering is necessary
    needsEffectRender =
      cameraPositionChanged ||
      cameraRotationChanged ||
      controlsChanged ||
      earthModelChanging ||
      controls.isDragging; // Always render when actively dragging

    // Update tracking variables
    lastCameraPosition.copy(camera.position);
    lastCameraRotation.copy(camera.rotation);
    lastControlsAzimuthal = currentAzimuthal;
    lastControlsPolar = currentPolar;

    requestAnimationFrame(animate);
    renderer.render(scene, camera);

    // Only render ASCII effect when globe is visible in viewport (memory optimization)
    if (isGlobeVisible) {
      effect.render(scene, camera);
    }

    css2dRenderer.render(scene, camera);
  }

  // Track last resize dimensions to prevent unnecessary updates from scroll
  let lastResizeWidth = width;
  let lastResizeHeight = height;
  let resizeTimeout = null;

  function onWindowResize() {
    // Debounce resize to prevent multiple rapid calls
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const newWidth = container.clientWidth || window.innerWidth;
      // Use stable height calculation that ignores Safari address bar changes
      const newHeight = getStableHeight();

      // Only update if dimensions actually changed significantly (more than 50px for height)
      // This prevents updates from Safari address bar show/hide (typically ~50-100px change)
      const widthChanged = Math.abs(newWidth - lastResizeWidth) > 10;
      const heightChanged = Math.abs(newHeight - lastResizeHeight) > 50; // Larger threshold for height

      if (widthChanged || heightChanged) {
        lastResizeWidth = newWidth;
        lastResizeHeight = newHeight;

        // console.log("globe resize",container.clientWidth,newWidth, container.clientHeight,newHeight,newWidth / newHeight)
        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();

        renderer.setSize(newWidth, newHeight);
        effect.setSize(newWidth, newHeight);
        css2dRenderer.setSize(newWidth, newHeight);
      }
    }, 150); // Debounce for 150ms
  }

  window.addEventListener("resize", onWindowResize);

  // Use visualViewport API for more reliable resize detection on iOS Safari
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onWindowResize);
  }

  animate();

  return () => {
    window.removeEventListener("resize", onWindowResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", onWindowResize);
    }

    // Cleanup visibility observer
    if (visibilityObserver) {
      visibilityObserver.disconnect();
    }

    gsap.killTweensOf(cameraAnimProgress);

    // Cleanup effect to prevent memory leaks
    if (effect && effect.domElement) {
      if (container.contains(effect.domElement)) {
        container.removeChild(effect.domElement);
      }
      // Dispose effect resources if available
      if (effect.dispose) {
        effect.dispose();
      }
    }
    if (parentContainer.contains(css2dRenderer.domElement)) {
      parentContainer.removeChild(css2dRenderer.domElement);
    }

    css2dLabels.forEach((label) => scene.remove(label));

    if (earthModel) {
      earthModel.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((material) => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      scene.remove(earthModel);
    }

    renderer.dispose();
  };
}
window.addEventListener("load", function () {
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({
    autoRefreshEvents: "DOMContentLoaded,load,visibilitychange",
  });
  initGlobe();
});
export { initGlobe };
